/**
 * FCT Style Grammar — school × weight-tier → {tint, glyph, verb, format}.
 *
 * Spec: docs/plans/fx-grammar-specs/fct-choreography.md (Phase 1).
 * Resolved ONCE in the FCT owner (DamagePresentationService), executed by
 * the verb engine in rendering/utils/{fctMotionVerbs,FloatingDamageText}.
 *
 * Color SSOT: this file adds NO color values. All tints delegate to
 * resolveDamageColor / resolveIncomingDamageColor / resolveHealColor
 * (combat-feedback.config.ts `damageTypeColors` + colorblind palette) —
 * a second school color table is exactly the drift PF-SCHOOL-IDENTITY-DRIFT
 * exists to prevent.
 *
 * Magnitude SSOT: weight tiers derive from HitWeight (resolveHitWeight SSOT,
 * no second magnitude table), so FCT weight always agrees with shake/flinch weight.
 * The adaptive rolling-percentile blend is deferred to v1.1 behind `rolling.enabled`.
 *
 * SCHOOL_GLYPHS is the 5th drift-guarded school identity map: its key-set
 * must cover every school in `damageTypeColors`. Cross-checked in
 * fctStyle.config.test.ts; the conductor wires the same check into
 * scripts/vfx-preflight.ts PF-SCHOOL-IDENTITY-DRIFT (4→5 maps).
 *
 * Two-section file (defaults + dev-override pattern), per
 * docs/plans/2026-04-25-CONFIG-MIGRATION.md.
 */

import {
  resolveDamageColor,
  resolveIncomingDamageColor,
  resolveHealColor,
} from '../../shared/config/combat-feedback.config';
import { type HitWeight, resolveHitWeight } from '../../shared/combat/hitWeight';

// ─── Section 1: types ────────────────────────────────────────────────────

export type FctMotionVerb =
  | 'pop'          // 250ms scale overshoot then rise+fade — bread-and-butter hit
  | 'arc'          // rise with seeded lateral drift + gravity tail — heavy hits fan
  | 'drift'        // slow rise, early long fade — chip damage / ticks
  | 'hangThenFall' // fast rise, hang at full alpha, drop while fading — big hits
  | 'hangThenPunch'// crit stamp: big overshoot, hang, fall
  | 'softRise';    // heal: drift + gentle scale breathe

export interface FctTierStyle {
  fontSize: number;
  verb: FctMotionVerb;
  durationMs: number;
}

export interface FctStyleConfig {
  /** Tier 0 chip / 1 solid / 2 heavy / 3 massive — indexed by weight tier. */
  tiers: [FctTierStyle, FctTierStyle, FctTierStyle, FctTierStyle];
  /** Verb override for crits (always tier 3). */
  critVerb: FctMotionVerb;
  /** Crits keep the school tint (crit_overlay doctrine). Open Q1 gates flipping. */
  critKeepsSchoolTint: boolean;
  /**
   * 5th drift-guarded school identity map — 1-char accent glyph prefixed to
   * the number string. A SHAPE cue (colorblind channel), not a color: INameTag
   * is single-style text so the glyph shares the number's tint. Key-set must
   * cover `damageTypeColors` (preflight + jest cross-checked). Empty string =
   * deliberately no glyph (physical stays clean on the most common hit).
   */
  schoolGlyphs: Record<string, string>;
  /** Gold-role crit marker, rendered as a suffix (color stays school-owned). */
  critGlyph: string;
  /** Amounts ≥ this render as `12.3k` (one decimal, truncated). */
  abbreviateAt: number;
  /** Hard cap on live floating labels. */
  maxConcurrent: number;
  /** Spawns-per-trailing-second above which density degradation engages. */
  degradeSpawnsPerSec: number;
  /** Per-target lateral spawn slots (px). Arc drift follows slot sign. */
  slotOffsetsX: number[];
  /** Slot ring memory — consecutive hits within this window advance the slot. */
  slotMemoryMs: number;
  /** v1.1 adaptive percentile baseline — NOT active in v1 (spec §2.b). */
  rolling: { enabled: boolean; window: number; refreshMs: number };
}

// ─── Section 2: defaults + dev override ──────────────────────────────────

export const FCT_STYLE_DEFAULTS: FctStyleConfig = {
  tiers: [
    { fontSize: 16, verb: 'drift', durationMs: 900 },  // 0 chip   (light)
    { fontSize: 22, verb: 'pop', durationMs: 1100 },   // 1 solid  (medium) — today's normal size
    { fontSize: 27, verb: 'arc', durationMs: 1300 },   // 2 heavy  (heavy)
    { fontSize: 32, verb: 'hangThenFall', durationMs: 1500 }, // 3 massive — today's crit size
  ],
  critVerb: 'hangThenPunch',
  critKeepsSchoolTint: true,
  schoolGlyphs: {
    physical: '', // spec allows '·' or none — none keeps the common hit clean
    fire: '▲',
    frost: '❆',
    arcane: '✦',
    shadow: '▾',
    holy: '✚',
    nature: '❧',
    chaos: '✖',
    various: '',
  },
  critGlyph: '✦',
  abbreviateAt: 10_000,
  maxConcurrent: 40,
  degradeSpawnsPerSec: 20,
  slotOffsetsX: [-18, -6, 6, 18],
  slotMemoryMs: 300,
  rolling: { enabled: false, window: 64, refreshMs: 1000 },
};

/** Partial dev overrides — anything omitted falls through to defaults. */
export const FCT_STYLE_DEV_OVERRIDE: Partial<FctStyleConfig> = {};

export const FCT_STYLE: FctStyleConfig = {
  ...FCT_STYLE_DEFAULTS,
  ...FCT_STYLE_DEV_OVERRIDE,
  tiers: (FCT_STYLE_DEV_OVERRIDE.tiers ?? FCT_STYLE_DEFAULTS.tiers),
  schoolGlyphs: { ...FCT_STYLE_DEFAULTS.schoolGlyphs, ...FCT_STYLE_DEV_OVERRIDE.schoolGlyphs },
  rolling: { ...FCT_STYLE_DEFAULTS.rolling, ...FCT_STYLE_DEV_OVERRIDE.rolling },
};

// ─── Section 3: pure resolver — the unit-test surface ────────────────────

export interface FctStyleInput {
  amount: number;
  school: string;
  weight?: HitWeight;
  flags: {
    crit?: boolean;
    heal?: boolean;
    /** HoT/DoT periodic tick — pinned tier 0 `drift`. */
    tick?: boolean;
    /** "You got hit" numbers — red family kept (Open Q2), school glyph still shown. */
    incoming?: boolean;
    /** Local-player number — never dropped by the cap/degrade gates. */
    local?: boolean;
    /** RESERVED (spec P4) — no wiring until the death-beats lane defines the signal. */
    killingBlow?: boolean;
  };
  colorblindSafe: boolean;
}

export interface ResolvedFctStyle {
  /** Fully formatted display string: glyph prefix, +/abbreviation, crit suffix. */
  text: string;
  /** Hex color string — delegated to the combat-feedback resolvers. */
  color: string;
  fontSize: number;
  verb: FctMotionVerb;
  durationMs: number;
  tier: 0 | 1 | 2 | 3;
  /** Cap/degrade gates never drop this spawn (crit / local-player / special beat). */
  alwaysRender: boolean;
}

export const FCT_TIER_FOR_WEIGHT: Record<HitWeight, 0 | 1 | 2 | 3> = {
  tap: 0,
  light: 0,
  medium: 1,
  heavy: 2,
  epic: 3,
};

/** `9999` → "9999"; `10000` → "10.0k"; `12345` → "12.3k" (truncated, one decimal). */
export function formatFctAmount(amount: number, abbreviateAt: number = FCT_STYLE.abbreviateAt): string {
  const n = Math.round(amount);
  if (n < abbreviateAt) return String(n);
  return `${(Math.floor(n / 100) / 10).toFixed(1)}k`;
}

/**
 * Resolve the full FCT style for one number. Pure — no window, no caches.
 * Colors delegate to the combat-feedback SSOT resolvers; this adds NO values.
 */
export function resolveFctStyle(input: FctStyleInput): ResolvedFctStyle {
  const { amount, school, flags, colorblindSafe } = input;
  const isCrit = !!flags.crit;
  const isHeal = !!flags.heal;
  const isTick = !!flags.tick;

  // ── Tier: derive from HitWeight (HitWeight SSOT, no second magnitude table).
  // Ticks pin to tier 0; crits force tier 3 (spec §2.b table + test plan).
  const tier: 0 | 1 | 2 | 3 = isTick
    ? 0
    : isCrit
      ? 3
      : FCT_TIER_FOR_WEIGHT[input.weight ?? resolveHitWeight({ damage: amount, critical: false })];

  const tierStyle = FCT_STYLE.tiers[tier];

  // ── Verb: tick → drift, heal → softRise, crit → critVerb, else tier verb.
  const verb: FctMotionVerb = isTick
    ? 'drift'
    : isHeal
      ? 'softRise'
      : isCrit
        ? FCT_STYLE.critVerb
        : tierStyle.verb;

  // ── Color: delegated. Crits keep school tint in normal mode
  // (critKeepsSchoolTint — fixes the crit-erases-school gap); colorblind
  // mode keeps its white crit override unchanged (resolver handles it).
  let color: string;
  if (isHeal) {
    color = resolveHealColor(colorblindSafe);
  } else if (flags.incoming) {
    color = resolveIncomingDamageColor(isCrit, colorblindSafe);
  } else if (colorblindSafe) {
    color = resolveDamageColor(school, isCrit, true);
  } else {
    color = resolveDamageColor(school, isCrit && !FCT_STYLE.critKeepsSchoolTint, false);
  }

  // ── Text: heal keeps `+`; damage gets school glyph prefix + crit suffix.
  const base = formatFctAmount(amount);
  let text: string;
  if (isHeal) {
    text = `+${base}`;
  } else {
    const glyph = FCT_STYLE.schoolGlyphs[school] ?? '';
    text = `${glyph}${base}${isCrit ? FCT_STYLE.critGlyph : ''}`;
  }

  return {
    text,
    color,
    fontSize: tierStyle.fontSize,
    verb,
    durationMs: tierStyle.durationMs,
    tier,
    alwaysRender: isCrit || !!flags.local,
  };
}

// ─── Phase 2: beat styles — the second text executor joins the grammar ───
//
// CombatEventVFXHandler's one-shot beats (lifesteal / bonus / mana /
// interrupt / local ticks / reaction word) historically rode
// FloatingTextManager's OWN motion table (own sizes, yoyo bounce, depth 50).
// Phase 2 retires that: every beat resolves a verb here and executes through
// the one executor (spawnFloatingDamageText), spec §2.d + integrations 8/10/11.

export type FctBeatKind =
  | 'lifesteal'
  | 'bonus_damage'
  | 'mana_restore'
  | 'interrupted'
  | 'status_tick'
  | 'tick_label'
  | 'reaction_label'
  | 'ability_name'
  | 'xp'
  | 'info';

export interface FctBeatStyle {
  verb: FctMotionVerb;
  /** Override font size (px); undefined keeps the beat's existing size. */
  fontSize?: number;
  durationMs?: number;
  /** Beats that ride above the number lane carry their own Y offset (px). */
  offsetY?: number;
}

/**
 * Spec §2.d beat table: heals/mana stay deliberately un-punchy
 * (softRise/drift), interrupts pop (copy unchanged), ticks pin drift,
 * and the reaction word is a small pop label above the number lane.
 */
export const FCT_BEAT_STYLES: Record<FctBeatKind, FctBeatStyle> = {
  lifesteal: { verb: 'softRise' },
  bonus_damage: { verb: 'pop' },
  mana_restore: { verb: 'drift' },
  interrupted: { verb: 'pop' },
  status_tick: { verb: 'drift' },
  tick_label: { verb: 'drift' },
  reaction_label: { verb: 'pop', fontSize: 13, durationMs: 900, offsetY: 44 },
  ability_name: { verb: 'drift' },
  xp: { verb: 'drift' },
  info: { verb: 'drift' },
};

/**
 * FloatingTextManager preset style → verb (Phase 2 delegation). The manager's
 * size/color presets are unchanged; ONLY motion migrates onto the verb
 * registry (the yoyo `bounce` flash is retired — accessibility rule §3.4).
 */
export const FCT_PRESET_STYLE_VERBS: Record<string, FctMotionVerb> = {
  damage: 'pop',
  heal: 'softRise',
  critical: 'hangThenPunch',
  ability: 'drift',
  xp: 'drift',
  info: 'drift',
};

/** 'react_shatter' → 'SHATTER' — the caps word for the reaction label beat. */
export function reactionLabelText(effectId: string): string {
  return effectId.replace(/^react_/, '').toUpperCase();
}
